/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description Filter constants
 */

export interface FilterTypes {
  AGE_RESTRICTED: 'ageRestricted'
  AVAILABLE_AFTER: 'availableAfter'
  AVAILABLE_BEFORE: 'availableBefore'
  BEDROOMS: 'bedrooms'
  BATHROOMS: 'bathroomsRange'
  BUILDING_AMENITIES: 'buildingAmenities'
  CATS: 'cats'
  DOGS: 'dogs'
  END_DATE: 'endDate'
  FEATURES: 'features'
  FEEDS: 'feeds'
  FLOORPLANS: 'floorplans'
  HAS_IMAGES: 'hasImages'
  HAS_VIRTUAL_TOURS: 'hasVirtualTours'
  HIDE_NO_VACANCY: 'hideNoVacancy'
  INCOME_RESTRICTED: 'incomeRestricted'
  IS_ZAPPABLE: 'isZappable'
  KEYWORDS: 'keywords'
  LISTING_AMENITIES: 'listingAmenities'
  LONG_TERM: 'longTerm'
  MIN_ACTIVE_UNITS: 'minActiveUnits'
  MAX_LAT: 'maxLat'
  MAX_LNG: 'maxLng'
  MAX_DAYS: 'maxDays'
  MAX_PRICE: 'maxPrice'
  MAX_PRICE_PER_BEDROOM: 'maxPricePerBedroom'
  MAX_SQUARE_FEET: 'maxSquareFeet'
  MIN_BATHROOMS: 'minBathrooms'
  MIN_LAT: 'minLat'
  MIN_LNG: 'minLng'
  MIN_PRICE: 'minPrice'
  MIN_SQUARE_FEET: 'minSquareFeet'
  MM_FROM: 'mmFrom'
  NEIGHBORHOOD_IDS: 'neighborhoodIds'
  NO_FEES: 'noFees'
  PAD: 'pad'
  PETS: 'pets'
  PET_TEXT: 'petText'
  PRICE: 'price'
  PROPERTY_CATEGORIES: {
    name: 'propertyCategories'
    APARTMENT: 'apartment'
    CONDO: 'condo'
    HOUSE: 'house'
    ROOM: 'room'
    OTHER: 'other'
  }
  PROPERTY_TYPES: 'propertyTypes'
  Q: 'Q'
  QS: 'qs'
  SECTION8: 'section8'
  SHORT_TERM: 'shortTerm'
  SORT: 'sort'
  SOURCES: {
    name: 'sources'
    APARTMENT_SEARCH: 'apartmentSearch'
    HOMESUITE: 'homesuite'
    FOR_RENT: 'forRent'
    OTHER: 'other'
  }
  START_DATE: 'startDate'
  STUDENT_HOUSING: 'studentHousing'
  URL: 'url'
}

export const FILTER_PANEL = 'Filter Panel'
export const FILTER_BODY_ELEMENT_ID = 'filterBody'

export const APARTMENTS = 'apartments'
export const CONDOS = 'condos'
export const HOUSES = 'houses'
export const ROOMS = 'rooms'
export const PROPERTY_TYPES = [APARTMENTS, CONDOS, HOUSES, ROOMS]

export const DEFAULT_SORT_OPTION = 'relevance1'
export const NEWEST_SORT_OPTION = 'newest'
export const OLDEST_SORT_OPTION = 'listed_on'
export const POPULAR_SORT_OPTION = 'popular'
export const PRICE_SORT_ASC_OPTION = 'price'
export const PRICE_SORT_DESC_OPTION = '-price'
export const RELEVANCE2_SORT_OPTION = 'relevance2'
export const RELEVANCE_SORT_OPTION = 'relevance1'
export const RATING_SORT_ASC_OPTION = 'rating'
export const RATING_SORT_DESC_OPTION = '-rating'

// sort type
export const DEFAULT_SORT = [DEFAULT_SORT_OPTION]
export const NEWEST_SORT = [NEWEST_SORT_OPTION]
export const OLDEST_SORT = [OLDEST_SORT_OPTION]
export const POPULAR_SORT = [POPULAR_SORT_OPTION]
export const PRICE_SORT_ASC = [PRICE_SORT_ASC_OPTION]
export const PRICE_SORT_DESC = [PRICE_SORT_DESC_OPTION]
export const RELEVANCE2_SORT = [RELEVANCE2_SORT_OPTION]
export const RELEVANCE_SORT = [RELEVANCE_SORT_OPTION]

export const SORT_OPTIONS = {
  [NEWEST_SORT_OPTION]: 'Newest first',
  [PRICE_SORT_ASC_OPTION]: 'Price (lowest)',
  [PRICE_SORT_DESC_OPTION]: 'Price (highest)',
  [RELEVANCE_SORT_OPTION]: 'Relevance',
}

export const ALLOWED_SORT_OPTIONS = new Set([
  DEFAULT_SORT_OPTION,
  NEWEST_SORT_OPTION,
  OLDEST_SORT_OPTION,
  POPULAR_SORT_OPTION,
  PRICE_SORT_ASC_OPTION,
  PRICE_SORT_DESC_OPTION,
  RELEVANCE2_SORT_OPTION,
  RELEVANCE_SORT_OPTION,
  RATING_SORT_ASC_OPTION,
  RATING_SORT_DESC_OPTION,
])

export const MAX_FILTER_PRICE: number = 10000
export const MIN_FILTER_PRICE: number = 0
export const MAX_NIGHTLY_FILTER_PRICE: number = 1000

export const MAX_FILTER_CUSTOM_PRICE: number = 1000000
export const MIN_FILTER_CUSTOM_PRICE: number = 0

export const ACCESSIBLE = 'Accessible'
export const PETS_ALLOWED = 'Pets allowed'
export const FREE_CANCELLATION = 'Free Cancellation'
export const POPULAR_OPTIONS = [PETS_ALLOWED, ACCESSIBLE, FREE_CANCELLATION]
/**
 * ref: https://github.com/zumper/zumper_code/issues/14627
 * Since we don't have a city that has more than 600 neighborhoods in a city, setting
 * the limit as 600 to not over-engineer this. THIS NEEDS TO BE ADDRESSED
 */
export const MAX_NEIGHBORHOOD_LIMIT: number = 600

// BEDROOM CONSTANT
export const ROOM: number = -1
export const STUDIO: number = 0
export const ONE_BR: number = 1
export const TWO_BR: number = 2
export const THREE_BR: number = 3
export const FOUR_BR: number = 4 // 4
export const FIVE_BR: number = 5 // 4+

export const STUDIO_STR = 'studios'
export const ONE_BR_STR = '1 beds'
export const TWO_BR_STR = '2 beds'
export const THREE_BR_STR = '3 beds'
export const FOUR_PLUS_BR_STR = '4+beds'

// min lease days to be considered short term
export const SHORT_TERM_LEASE_DAYS: number = 180

// min lease days to have a price per night
export const PRICE_PER_NIGHT_LEASE_DAYS: number = 30
export const DESKTOP_FILTER_HEIGHT: number = 310

// these should replace string literals throughout the code base. e.g. bizos/filter.js
export const FILTER_TYPES: FilterTypes = {
  AGE_RESTRICTED: 'ageRestricted',
  AVAILABLE_AFTER: 'availableAfter',
  AVAILABLE_BEFORE: 'availableBefore',
  BEDROOMS: 'bedrooms',
  BATHROOMS: 'bathroomsRange',
  BUILDING_AMENITIES: 'buildingAmenities',
  CATS: 'cats',
  DOGS: 'dogs',
  END_DATE: 'endDate',
  FEATURES: 'features',
  FEEDS: 'feeds',
  FLOORPLANS: 'floorplans',
  HAS_IMAGES: 'hasImages',
  HAS_VIRTUAL_TOURS: 'hasVirtualTours',
  HIDE_NO_VACANCY: 'hideNoVacancy',
  INCOME_RESTRICTED: 'incomeRestricted',
  IS_ZAPPABLE: 'isZappable',
  KEYWORDS: 'keywords',
  LISTING_AMENITIES: 'listingAmenities',
  LONG_TERM: 'longTerm',
  MIN_ACTIVE_UNITS: 'minActiveUnits',
  MAX_LAT: 'maxLat',
  MAX_LNG: 'maxLng',
  MAX_DAYS: 'maxDays',
  MAX_PRICE: 'maxPrice',
  MAX_PRICE_PER_BEDROOM: 'maxPricePerBedroom',
  MAX_SQUARE_FEET: 'maxSquareFeet',
  MIN_BATHROOMS: 'minBathrooms',
  MIN_LAT: 'minLat',
  MIN_LNG: 'minLng',
  MIN_PRICE: 'minPrice',
  MIN_SQUARE_FEET: 'minSquareFeet',
  MM_FROM: 'mmFrom',
  NEIGHBORHOOD_IDS: 'neighborhoodIds',
  NO_FEES: 'noFees',
  PAD: 'pad',
  PETS: 'pets',
  PET_TEXT: 'petText',
  PRICE: 'price',
  PROPERTY_CATEGORIES: {
    name: 'propertyCategories',
    APARTMENT: 'apartment',
    CONDO: 'condo',
    HOUSE: 'house',
    ROOM: 'room',
    OTHER: 'other',
  },
  PROPERTY_TYPES: 'propertyTypes',
  Q: 'Q',
  QS: 'qs',
  SECTION8: 'section8',
  SHORT_TERM: 'shortTerm',
  SORT: 'sort',
  SOURCES: {
    name: 'sources',
    APARTMENT_SEARCH: 'apartmentSearch',
    HOMESUITE: 'homesuite',
    FOR_RENT: 'forRent',
    OTHER: 'other',
  },
  START_DATE: 'startDate',
  STUDENT_HOUSING: 'studentHousing',
  URL: 'url',
}

export const FILTER_FACET_TEXT = {
  NO_FEE: 'No Fee',
  PET_FRIENDLY: 'Pet Friendly',
  CHEAP: 'Cheap',
  LUXURY: 'Luxury',
  LOW_INCOME: 'Low Income',
  MOVE_IN_SPECIAL: 'Move In Special',
  UTILITIES_INCLUDED: 'Utilities Included',
  NO_SECURITY_DEPOSIT: 'No Security Deposit',
  FURNISHED: 'Furnished',
  SHORT_TERM: 'Short Term',
}

export const VERTICAL_TYPES = {
  CHEAP: 'cheap',
  FURNISHED: 'furnished',
  LOW_INCOME: 'low income',
  LUXURY: 'luxury',
  MOVE_IN_SPECIAL: 'move in special',
  NEW_CONSTRUCTION: 'new construction',
  NO_FEE: 'no fee',
  NO_SECURITY_DEPOSIT: 'no security deposit',
  PET_FRIENDLY: 'pet friendly',
  SHORT_TERM: 'short term',
  UTILITIES_INCLUDED: 'utilities included',
}

export const VERTICAL_TEXT = {
  cheap: 'Cheap',
  furnished: 'Furnished',
  'low-income': 'Low-Income',
  luxury: 'Luxury',
  'move-in-special': 'Move-In Special',
  'no-fee': 'No Fee',
  'no-security-deposit': 'No Security Deposit',
  'pet-friendly': 'Pet-Friendly',
  'short-term': 'Short Term',
  'utilities-included': 'Utilities included',
}

export const FILTER_FACET_URL = {
  [FILTER_FACET_TEXT.NO_FEE]: 'no-fee',
  [FILTER_FACET_TEXT.PET_FRIENDLY]: 'pet-friendly',
  [FILTER_FACET_TEXT.CHEAP]: 'cheap',
  [FILTER_FACET_TEXT.LUXURY]: 'luxury',
  [FILTER_FACET_TEXT.LOW_INCOME]: 'low-income',
  [FILTER_FACET_TEXT.MOVE_IN_SPECIAL]: 'move-in-special',
  [FILTER_FACET_TEXT.UTILITIES_INCLUDED]: 'utilities-included',
  [FILTER_FACET_TEXT.NO_SECURITY_DEPOSIT]: 'no-security-deposit',
  [FILTER_FACET_TEXT.FURNISHED]: 'furnished',
  [FILTER_FACET_TEXT.SHORT_TERM]: 'short-term',
}

export const FILTER_ANALYTICS_CATEGORY = 'Filter'
export const FILTER_SUMMARY_ANALYTICS_CATEGORY = 'FilterSummary'

export const FLOORPLANS_OPTIONS = {
  HIDE: 0,
  ONLY: 1,
  SHOW: null,
}

export const MAX_BATHROOM_COUNT = 5

export const OTHERS_FILTERS_ANALYTICS_TEXT = {
  [FILTER_TYPES.HAS_IMAGES]: 'Has Images',
  [FILTER_TYPES.MAX_DAYS]: 'Posted Today',
  [FILTER_TYPES.NO_FEES]: 'No Fee',
}

export const URL_FILTER_QUERY_KEYS = {
  ageRestricted: 'age-restricted',
  availableAfter: 'available-after',
  availableBefore: 'available-before',
  minBathrooms: 'bathrooms',
  bathroomsRange: 'bathrooms-range',
  buildingAmenities: 'building-amenities',
  encodedFilters: 'encoded-filters',
  [FILTER_TYPES.END_DATE]: 'end-date',
  features: 'features',
  feeds: 'feeds',
  floorplans: 'floorplans',
  hasIframe: 'has-iframe',
  hasImages: 'images',
  hasVirtualTours: 'has-virtual-tours',
  hideNoVacancy: 'hide-no-vacancy',
  incomeRestricted: 'restricted-income',
  keywords: 'keywords',
  leaseTerm: 'lease-term',
  listingAmenities: 'listing-amenities',
  luxury: 'luxury',
  minActiveUnits: 'min-active-units',
  maxDays: 'max-days',
  maxPricePerBedroom: 'price-per-bedroom',
  maxSqft: 'max-square-feet',
  minSqft: 'min-square-feet',
  neighborhoodIds: 'neighborhood-ids',
  liveOnlineTours: 'live-online-tours',
  pad: 'pad',
  q: 'Q',
  qs: 'qs',
  recommended: 'recommended',
  section8: 'section8',
  sort: 'sort',
  [FILTER_TYPES.START_DATE]: 'start-date',
  studentHousing: 'student-housing',
  transits: 'transits',
  videoTours: 'video-tours',
  waitlisted: 'waitlisted',
  zappable: 'zappable',
}

export interface LEASE_LENGTH_SELECTIONS_TYPE {
  LONG_TERM: 'Long-term'
  SHORT_TERM: 'Short-term'
}

export const LEASE_LENGTH_SELECTIONS: LEASE_LENGTH_SELECTIONS_TYPE = {
  LONG_TERM: 'Long-term',
  SHORT_TERM: 'Short-term',
}

export const ZOOM_STATE = 6
export const FILTER_BAR = 'Filter Bar'
export const FILTER_MODAL_TYPES = {
  ROOMS: 'Rooms',
  PETS: 'Pets',
  PRICE_RANGE: 'Price Range',
  SORT: 'Sort',
} as const

export const PRICE_RANGE = [0, MAX_FILTER_PRICE]
export const NIGHTLY_PRICE_RANGE = [0, MAX_NIGHTLY_FILTER_PRICE]

export const BATHROOMS_OPTIONS = [
  { ariaLabel: '1 bathroom', label: '1', isSelected: false, value: 1 },
  { ariaLabel: '2 bathrooms', label: '2', isSelected: false, value: 2 },
  { ariaLabel: '3 bathrooms', label: '3', isSelected: false, value: 3 },
  { ariaLabel: '4 bathrooms', label: '4', isSelected: false, value: 4 },
  {
    ariaLabel: '5 bathrooms or more',
    label: '5+',
    isSelected: false,
    value: 5,
  },
]

export const BEDROOMS_OPTIONS = [
  { ariaLabel: 'studio', label: 'Studio', isSelected: false, value: 0 },
  { ariaLabel: '1 bedroom', label: '1', isSelected: false, value: 1 },
  { ariaLabel: '2 bedrooms', label: '2', isSelected: false, value: 2 },
  { ariaLabel: '3 bedrooms', label: '3', isSelected: false, value: 3 },
  { ariaLabel: '4 bedrooms or more', label: '4+', isSelected: false, value: 4 },
]

export const SORT_FILTER_OPTIONS = [
  {
    label: 'relevance1',
    title: 'Most relevant',
  },
  {
    label: 'newest',
    title: 'Newest first',
  },
  {
    label: 'price',
    title: 'Price: low to high',
  },
  {
    label: '-price',
    title: 'Price: high to low',
  },
]

export const BUILDING_OPTIONS = [
  { text: 'Show buildings', value: FLOORPLANS_OPTIONS.SHOW },
  { text: 'Only buildings', value: FLOORPLANS_OPTIONS.ONLY },
  { text: 'Hide buildings', value: FLOORPLANS_OPTIONS.HIDE },
]
